// src/modules/feedbacks/Feedbacks.tsx
import React, { useState } from 'react';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  Row,
  Col,
  Space,
  Tag,
  Typography,
  Statistic,
  Rate,
  Tooltip,
  Modal,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  CheckOutlined,
  CloseOutlined,
  StarOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import { useFeedbacks } from '@/hooks/useFeedbacks';
import { Feedback } from '@/services/api/feedbacks';
import dayjs from 'dayjs';

const { Option } = Select;
const { Title, Text, Paragraph } = Typography;

// Цвета для рейтингов (ОБНОВЛЕНО для 1-3)
const getRatingColor = (rating: number) => {
  if (rating === 3) return '#52c41a';  // Отлично - зелёный
  if (rating === 2) return '#faad14';  // Хорошо - жёлтый
  return '#f5222d';                    // Плохо - красный
};

interface FilterParams {
  rating?: number;
  is_approved: 'pending' | 'approved' | 'all';
  sort: 'newest' | 'oldest' | 'highest' | 'lowest';
  search: string;
  limit: number;
  offset: number;
}

export const Feedbacks: React.FC = () => {
  const {
    feedbacks,
    stats,
    pagination,
    loading,
    actionLoading,
    loadFeedbacks,
    approveFeedback,
    rejectFeedback,
    bulkApproveFeedbacks,
    bulkRejectFeedbacks,
    refresh,
  } = useFeedbacks();

  // Состояние фильтров
  const [filters, setFilters] = useState<FilterParams>({
    rating: undefined,
    is_approved: 'pending',
    sort: 'newest',
    search: '',
    limit: 20,
    offset: 0,
  });

  // Массовый выбор
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  
  // Модальное окно для просмотра полного отзыва
  const [viewModal, setViewModal] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState<Feedback | null>(null);

  // Применение фильтров
  const applyFilters = () => {
    const params: any = {
      ...filters,
      offset: 0, // Сброс на первую страницу
    };
    
    if (!params.rating) delete params.rating;
    if (!params.search) delete params.search;
    
    loadFeedbacks(params);
  };

  // Сброс фильтров
  const resetFilters = () => {
    setFilters({
      rating: undefined,
      is_approved: 'pending',
      sort: 'newest',
      search: '',
      limit: 20,
      offset: 0,
    });
    loadFeedbacks({ is_approved: 'pending' });
  };

  // Обработка изменения страницы
  const handleTableChange = (pag: any) => {
    const params = {
      ...filters,
      limit: pag.pageSize,
      offset: (pag.current - 1) * pag.pageSize,
    };
    
    setFilters(params);
    loadFeedbacks(params);
  };

  // Просмотр полного отзыва
  const viewFeedback = (feedback: Feedback) => {
    setCurrentFeedback(feedback);
    setViewModal(true);
  };

  // Одобрение одного отзыва
  const handleApprove = async (feedbackId: string) => {
    try {
      await approveFeedback(feedbackId);
    } catch (error) {
      // Ошибка уже обработана в хуке
    }
  };

  // Отклонение одного отзыва
  const handleReject = async (feedbackId: string) => {
    try {
      await rejectFeedback(feedbackId);
    } catch (error) {
      // Ошибка уже обработана в хуке
    }
  };

  // Массовое одобрение
  const handleBulkApprove = async () => {
    if (!selectedRowKeys.length) return;
    
    Modal.confirm({
      title: 'Массовое одобрение',
      content: `Вы уверены, что хотите одобрить ${selectedRowKeys.length} отзывов?`,
      okText: 'Одобрить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await bulkApproveFeedbacks(selectedRowKeys);
          setSelectedRowKeys([]);
        } catch (error) {
          // Ошибка уже обработана в хуке
        }
      },
    });
  };

  // Массовое отклонение
  const handleBulkReject = async () => {
    if (!selectedRowKeys.length) return;
    
    Modal.confirm({
      title: 'Массовое отклонение',
      content: `Вы уверены, что хотите отклонить ${selectedRowKeys.length} отзывов?`,
      okText: 'Отклонить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await bulkRejectFeedbacks(selectedRowKeys);
          setSelectedRowKeys([]);
        } catch (error) {
          // Ошибка уже обработана в хуке
        }
      },
    });
  };

  // Конфигурация выбора строк
  const rowSelection: TableRowSelection<Feedback> = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys as string[]);
    },
  };

  // Колонки таблицы
  const columns: ColumnsType<Feedback> = [
    {
      title: 'Заказ',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 120,
      render: (text: string) => (
        <Text strong className="text-blue-600">{text}</Text>
      ),
    },
    {
      title: 'Клиент',
      key: 'customer',
      width: 200,
      render: (_, record: Feedback) => (
        <div>
          <div className="font-medium">{record.customer_name}</div>
          <div className="text-sm text-gray-500">{record.customer_phone}</div>
        </div>
      ),
    },
    {
      title: 'Оценка',
      dataIndex: 'rating',
      key: 'rating',
      width: 150,
      align: 'center',
      render: (rating: number) => (
        <div>
          <Rate disabled value={rating} count={3} style={{ fontSize: 16 }} />
          <div className="text-sm font-medium mt-1" style={{ color: getRatingColor(rating) }}>
            {rating}.0
          </div>
        </div>
      ),
    },
    {
      title: 'Комментарий',
      dataIndex: 'comment',
      key: 'comment',
      ellipsis: true,
      render: (text: string, record: Feedback) => (
        text ? (
          <Tooltip title="Нажмите чтобы посмотреть полностью">
            <Button 
              type="link" 
              onClick={() => viewFeedback(record)}
              className="p-0 h-auto text-left"
            >
              <Paragraph 
                ellipsis={{ rows: 2 }}
                className="mb-0"
              >
                {text}
              </Paragraph>
            </Button>
          </Tooltip>
        ) : (
          <Text type="secondary" italic>Без комментария</Text>
        )
      ),
    },
    {
      title: 'Дата',
      dataIndex: 'date',
      key: 'date',
      width: 150,
      render: (date: string, record: Feedback) => (
        <div>
          <div>{dayjs(date).format('DD.MM.YYYY')}</div>
          <div className="text-xs text-gray-500">{dayjs(date).format('HH:mm')}</div>
          {record.response_time_minutes && (
            <div className="text-xs text-blue-600">
              Ответ: {record.response_time_minutes} мин
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'is_approved',
      key: 'is_approved',
      width: 120,
      align: 'center',
      render: (is_approved: boolean, record: Feedback) => (
        <div>
          {is_approved ? (
            <Tag color="success" icon={<CheckOutlined />}>
              Одобрен
            </Tag>
          ) : (
            <Tag color="warning">
              На модерации
            </Tag>
          )}
          {record.moderated_at && (
            <div className="text-xs text-gray-500 mt-1">
              {dayjs(record.moderated_at).format('DD.MM HH:mm')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      align: 'center',
      render: (_, record: Feedback) => (
        <Space>
          <Tooltip title="Просмотреть">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => viewFeedback(record)}
            />
          </Tooltip>
          {!record.is_approved && (
            <>
              <Tooltip title="Одобрить">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  onClick={() => handleApprove(record.id)}
                  loading={actionLoading}
                  style={{ color: '#52c41a' }}
                />
              </Tooltip>
              <Tooltip title="Отклонить">
                <Button
                  type="text"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => handleReject(record.id)}
                  loading={actionLoading}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2}>
          <StarOutlined className="mr-2" />
          Управление отзывами
        </Title>

        {/* Статистика */}
        {stats && (
          <Row gutter={16} className="mt-4">
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="Всего отзывов"
                  value={stats.total_reviews}
                  prefix={<StarOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="Средняя оценка"
                  value={stats.average_rating}
                  precision={1}
                  valueStyle={{ color: getRatingColor(stats.average_rating) }}
                  suffix="/ 3"
                />
              </Card>
            </Col>
            <Col span={4}>
              <Card size="small">
                <Statistic
                  title="На модерации"
                  value={stats.pending_moderation}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" title="Распределение оценок">
                <Space direction="horizontal" size="large">
                  {([3, 2, 1] as const).map(rating => {
                    const ratingLabels: { [key: number]: string } = {
                      3: 'Отлично',
                      2: 'Хорошо',
                      1: 'Плохо'
                    };
                    
                    return (
                      <div key={rating} className="text-center">
                        <div className="text-lg font-bold" style={{ color: getRatingColor(rating) }}>
                          {stats.distribution[rating.toString() as '1' | '2' | '3']}
                        </div>
                        <div className="text-xs text-gray-500">{ratingLabels[rating]}</div>
                      </div>
                    );
                  })}
                </Space>
              </Card>
            </Col>
          </Row>
        )}
      </div>

      {/* Фильтры */}
      <Card className="mb-4">
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Input
              placeholder="Поиск по заказу, имени, телефону..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              onPressEnter={applyFilters}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Оценка"
              style={{ width: '100%' }}
              value={filters.rating}
              onChange={(value) => setFilters(prev => ({ ...prev, rating: value }))}
              allowClear
            >
              <Option value={3}>Отлично ⭐⭐⭐</Option>
              <Option value={2}>Хорошо ⭐⭐</Option>
              <Option value={1}>Плохо ⭐</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Статус модерации"
              style={{ width: '100%' }}
              value={filters.is_approved}
              onChange={(value) => setFilters(prev => ({ ...prev, is_approved: value }))}
            >
              <Option value="pending">На модерации</Option>
              <Option value="approved">Одобренные</Option>
              <Option value="all">Все</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Сортировка"
              style={{ width: '100%' }}
              value={filters.sort}
              onChange={(value) => setFilters(prev => ({ ...prev, sort: value }))}
            >
              <Option value="newest">Сначала новые</Option>
              <Option value="oldest">Сначала старые</Option>
              <Option value="highest">Высокая оценка</Option>
              <Option value="lowest">Низкая оценка</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Space>
              <Button 
                type="primary" 
                onClick={applyFilters}
                loading={loading}
              >
                Применить
              </Button>
              <Button onClick={resetFilters}>
                Сбросить
              </Button>
              <Button 
                icon={<ReloadOutlined />}
                onClick={refresh}
                loading={loading}
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Массовые действия */}
      {selectedRowKeys.length > 0 && (
        <Card className="mb-4">
          <Row align="middle" gutter={16}>
            <Col>
              <Text strong>Выбрано: {selectedRowKeys.length} отзывов</Text>
            </Col>
            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={handleBulkApprove}
                  loading={actionLoading}
                >
                  Одобрить все
                </Button>
                <Button
                  danger
                  icon={<CloseOutlined />}
                  onClick={handleBulkReject}
                  loading={actionLoading}
                >
                  Отклонить все
                </Button>
                <Button onClick={() => setSelectedRowKeys([])}>
                  Отменить выбор
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {/* Таблица отзывов */}
      <Card>
        <Table
          columns={columns}
          dataSource={feedbacks}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          scroll={{ x: 1200 }}
          pagination={{
            current: Math.floor(pagination.offset / pagination.limit) + 1,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} отзывов`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Модальное окно просмотра отзыва */}
      <Modal
        title="Просмотр отзыва"
        open={viewModal}
        onCancel={() => {
          setViewModal(false);
          setCurrentFeedback(null);
        }}
        width={600}
        footer={[
          <Button key="close" onClick={() => setViewModal(false)}>
            Закрыть
          </Button>,
          currentFeedback && !currentFeedback.is_approved && (
            <>
              <Button
                key="reject"
                danger
                icon={<CloseOutlined />}
                onClick={() => {
                  handleReject(currentFeedback.id);
                  setViewModal(false);
                }}
                loading={actionLoading}
              >
                Отклонить
              </Button>
              <Button
                key="approve"
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => {
                  handleApprove(currentFeedback.id);
                  setViewModal(false);
                }}
                loading={actionLoading}
              >
                Одобрить
              </Button>
            </>
          ),
        ]}
      >
        {currentFeedback && (
          <div>
            <div className="mb-4">
              <Text type="secondary">Заказ:</Text>
              <div className="text-lg font-medium text-blue-600">
                {currentFeedback.order_number}
              </div>
            </div>
            
            <div className="mb-4">
              <Text type="secondary">Клиент:</Text>
              <div className="font-medium">{currentFeedback.customer_name}</div>
              <div className="text-sm text-gray-500">{currentFeedback.customer_phone}</div>
            </div>

            <div className="mb-4">
              <Text type="secondary">Оценка:</Text>
              <div className="mt-2">
                <Rate disabled value={currentFeedback.rating} count={3} style={{ fontSize: 20 }} />
                <span className="ml-2 text-lg font-bold" style={{ color: getRatingColor(currentFeedback.rating) }}>
                  {currentFeedback.rating}.0 / 3.0
                </span>
              </div>
            </div>

            {currentFeedback.comment && (
              <div className="mb-4">
                <Text type="secondary">Комментарий:</Text>
                <div className="mt-2 p-3 bg-gray-50 rounded border">
                  <Text>{currentFeedback.comment}</Text>
                </div>
              </div>
            )}

            <div className="mb-4">
              <Text type="secondary">Дата отзыва:</Text>
              <div>{dayjs(currentFeedback.date).format('DD.MM.YYYY HH:mm')}</div>
            </div>

            {currentFeedback.response_time_minutes && (
              <div className="mb-4">
                <Text type="secondary">Время ответа:</Text>
                <div className="text-blue-600">{currentFeedback.response_time_minutes} минут</div>
              </div>
            )}

            <div className="mb-4">
              <Text type="secondary">Статус:</Text>
              <div className="mt-2">
                {currentFeedback.is_approved ? (
                  <Tag color="success" icon={<CheckOutlined />}>Одобрен</Tag>
                ) : (
                  <Tag color="warning">На модерации</Tag>
                )}
              </div>
            </div>

            {currentFeedback.moderated_at && (
              <div>
                <Text type="secondary">Модерация:</Text>
                <div className="text-sm text-gray-500">
                  {dayjs(currentFeedback.moderated_at).format('DD.MM.YYYY HH:mm')}
                  {currentFeedback.moderated_by && ` - ${currentFeedback.moderated_by}`}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};